import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * The interval ID for countdown updates.
   * @type {number|undefined}
   */
  #timerInterval = undefined;

  #countdownEnd = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.setupCountdownTimer();
    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    this.suspend();
    this.destroyCountdownTimer();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  setupCountdownTimer() {
    const timerElement = this.querySelector('[data-countdown]');
    if (!timerElement) return;

    const storageKey = 'announcement-bar-countdown-end';
    const now = Date.now();
    let endTime;

    try {
      const stored = localStorage.getItem(storageKey);
      if (stored && Number(stored) > now) {
        endTime = Number(stored);
      } else {
        endTime = now + 24 * 60 * 60 * 1000;
        localStorage.setItem(storageKey, `${endTime}`);
      }
    } catch {
      endTime = now + 24 * 60 * 60 * 1000;
    }

    this.#countdownEnd = endTime;
    this.updateCountdown(timerElement);

    this.#timerInterval = setInterval(() => {
      this.updateCountdown(timerElement);
    }, 1000);
  }

  destroyCountdownTimer() {
    clearInterval(this.#timerInterval);
    this.#timerInterval = undefined;
  }

  updateCountdown(timerElement) {
    if (!timerElement || this.#countdownEnd === undefined) return;

    const hoursElement = timerElement.querySelector('[data-countdown-hours]');
    const minutesElement = timerElement.querySelector('[data-countdown-minutes]');
    const secondsElement = timerElement.querySelector('[data-countdown-seconds]');
    const labelElement = timerElement.querySelector('[data-countdown-label]');

    if (!hoursElement || !minutesElement || !secondsElement || !labelElement) return;

    const remainingMs = Math.max(0, this.#countdownEnd - Date.now());
    const seconds = Math.floor(remainingMs / 1000);
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    hoursElement.textContent = String(hours).padStart(2, '0');
    minutesElement.textContent = String(minutes).padStart(2, '0');
    secondsElement.textContent = String(secs).padStart(2, '0');
    labelElement.textContent = remainingMs > 0 ? '' : '';

    if (remainingMs <= 0) {
      clearInterval(this.#timerInterval);
      this.#timerInterval = undefined;
      timerElement.classList.add('announcement-bar__timer--finished');
    }
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
